import { useCallback, useEffect, useRef, useState } from "react";
import { MoveUpRight, Circle, Type, Crop, RotateCw, Undo2, Camera, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toCanvas } from "../lib/image";

const RED = "#EF4444";

function drawShape(ctx, s, lw) {
  ctx.strokeStyle = s.type === "crop" ? "#2DD4BF" : RED;
  ctx.fillStyle = RED;
  ctx.lineWidth = lw;
  ctx.setLineDash(s.type === "crop" ? [lw * 2, lw * 2] : []);
  if (s.type === "arrow") {
    const a = Math.atan2(s.y1 - s.y0, s.x1 - s.x0), h = lw * 5;
    ctx.beginPath(); ctx.moveTo(s.x0, s.y0); ctx.lineTo(s.x1, s.y1); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(s.x1, s.y1);
    ctx.lineTo(s.x1 - h * Math.cos(a - 0.45), s.y1 - h * Math.sin(a - 0.45));
    ctx.lineTo(s.x1 - h * Math.cos(a + 0.45), s.y1 - h * Math.sin(a + 0.45));
    ctx.closePath(); ctx.fill();
  } else if (s.type === "circle") {
    ctx.beginPath();
    ctx.ellipse((s.x0 + s.x1) / 2, (s.y0 + s.y1) / 2, Math.abs(s.x1 - s.x0) / 2 || 1, Math.abs(s.y1 - s.y0) / 2 || 1, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (s.type === "crop") {
    ctx.strokeRect(s.x0, s.y0, s.x1 - s.x0, s.y1 - s.y0);
  } else if (s.type === "text") {
    const fs = lw * 7;
    ctx.font = `bold ${fs}px "Plus Jakarta Sans", sans-serif`;
    const w = ctx.measureText(s.text).width;
    ctx.fillStyle = "rgba(15,23,42,.85)";
    ctx.fillRect(s.x0 - 6, s.y0 - fs, w + 12, fs * 1.35);
    ctx.fillStyle = "#fff";
    ctx.fillText(s.text, s.x0, s.y0);
  }
  ctx.setLineDash([]);
}

function stampCanvas(base, shapes, stamp) {
  const lw = Math.max(3, base.width / 220);
  const bar = Math.round(base.width / 22);
  const c = document.createElement("canvas");
  c.width = base.width;
  c.height = base.height + bar;
  const ctx = c.getContext("2d");
  ctx.drawImage(base, 0, 0);
  shapes.forEach((s) => drawShape(ctx, s, lw));
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(0, base.height, c.width, bar);
  ctx.fillStyle = "#fff";
  ctx.font = `600 ${Math.round(bar * 0.42)}px "JetBrains Mono", monospace`;
  ctx.textBaseline = "middle";
  ctx.fillText(stamp, bar * 0.4, base.height + bar / 2);
  ctx.globalAlpha = 0.35;
  ctx.font = `800 ${Math.round(bar * 0.5)}px "Cabinet Grotesk", sans-serif`;
  ctx.textAlign = "right";
  ctx.fillText("POSSESSIONCHECK.COM", c.width - bar * 0.4, base.height - bar * 0.6);
  ctx.globalAlpha = 1;
  return c;
}

export default function PhotoAnnotator({ src, open, onCancel, onSave, onRetake, stamp }) {
  const ref = useRef(null);
  const [base, setBase] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [tool, setTool] = useState("arrow");
  const [label, setLabel] = useState("Defect");
  const drag = useRef(null);

  useEffect(() => {
    if (!src) return;
    setShapes([]);
    toCanvas(src).then(setBase);
  }, [src]);

  const draw = useCallback(() => {
    const c = ref.current;
    if (!c || !base) return;
    c.width = base.width;
    c.height = base.height;
    const ctx = c.getContext("2d");
    ctx.drawImage(base, 0, 0);
    const lw = Math.max(3, base.width / 220);
    shapes.forEach((s) => drawShape(ctx, s, lw));
    if (drag.current) drawShape(ctx, drag.current, lw);
  }, [base, shapes]);

  useEffect(() => { draw(); }, [draw]);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) * ref.current.width) / r.width, y: ((e.clientY - r.top) * ref.current.height) / r.height };
  };
  const down = (e) => {
    const p = pos(e);
    if (tool === "text") { setShapes((s) => [...s, { type: "text", x0: p.x, y0: p.y, text: label || "Defect" }]); return; }
    ref.current.setPointerCapture(e.pointerId);
    drag.current = { type: tool, x0: p.x, y0: p.y, x1: p.x, y1: p.y };
  };
  const move = (e) => { if (!drag.current) return; const p = pos(e); drag.current = { ...drag.current, x1: p.x, y1: p.y }; draw(); };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.type === "crop") {
      const x = Math.min(d.x0, d.x1), y = Math.min(d.y0, d.y1), w = Math.abs(d.x1 - d.x0), h = Math.abs(d.y1 - d.y0);
      if (w < 40 || h < 40) { draw(); return; }
      const c = document.createElement("canvas");
      c.width = w; c.height = h;
      c.getContext("2d").drawImage(base, x, y, w, h, 0, 0, w, h);
      setShapes([]);
      setBase(c);
      setTool("arrow");
      return;
    }
    setShapes((s) => [...s, d]);
  };
  const rotate = () => {
    const c = document.createElement("canvas");
    c.width = base.height; c.height = base.width;
    const ctx = c.getContext("2d");
    ctx.translate(c.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(base, 0, 0);
    setShapes([]);
    setBase(c);
  };
  const save = () => onSave(stampCanvas(base, shapes, stamp).toDataURL("image/jpeg", 0.82));
  const T = ({ id, icon: I, label: l }) => (
    <button type="button" onClick={() => setTool(id)} data-testid={`photo-annotation-${id}-tool`}
      className={`tap flex h-11 flex-col items-center justify-center gap-0.5 rounded-md px-3 text-[10px] font-bold ${tool === id ? "bg-teal-500 text-slate-950" : "bg-slate-800 text-slate-200"}`}><I className="h-4 w-4" />{l}</button>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-h-[96vh] max-w-3xl overflow-y-auto border-slate-800 bg-slate-950 p-3 text-white sm:p-5" data-testid="photo-annotator">
        <DialogHeader><DialogTitle className="font-mono text-xs tracking-widest text-teal-300">PHOTO PREVIEW · ANNOTATE</DialogTitle></DialogHeader>
        <div className="flex justify-center rounded-md bg-black">
          <canvas ref={ref} onPointerDown={down} onPointerMove={move} onPointerUp={up} className="max-h-[52vh] max-w-full touch-none cursor-crosshair" data-testid="photo-annotation-canvas" />
        </div>
        <div className="font-mono text-[10px] text-slate-400">{stamp}</div>
        <div className="flex flex-wrap gap-1.5">
          <T id="arrow" icon={MoveUpRight} label="ARROW" />
          <T id="circle" icon={Circle} label="CIRCLE" />
          <T id="text" icon={Type} label="TEXT" />
          <T id="crop" icon={Crop} label="CROP" />
          <button type="button" onClick={rotate} className="tap flex h-11 flex-col items-center justify-center rounded-md bg-slate-800 px-3 text-[10px] font-bold" data-testid="photo-annotation-rotate-button"><RotateCw className="h-4 w-4" />ROTATE</button>
          <button type="button" onClick={() => setShapes((s) => s.slice(0, -1))} className="tap flex h-11 flex-col items-center justify-center rounded-md bg-slate-800 px-3 text-[10px] font-bold" data-testid="photo-annotation-undo-button"><Undo2 className="h-4 w-4" />UNDO</button>
          {tool === "text" && <input value={label} onChange={(e) => setLabel(e.target.value)} className="h-11 flex-1 rounded-md border border-slate-700 bg-slate-900 px-3 text-sm" placeholder="Label text, then tap photo" data-testid="photo-annotation-text-input" />}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button type="button" onClick={onRetake} className="tap flex h-12 items-center justify-center gap-2 rounded-md border border-slate-700 font-semibold" data-testid="photo-retake-button"><Camera className="h-4 w-4" />Retake</button>
          <button type="button" onClick={save} disabled={!base} className="tap flex h-12 items-center justify-center gap-2 rounded-md bg-teal-500 font-bold text-slate-950" data-testid="photo-use-button"><Check className="h-4 w-4" />Use photo</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
